import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignOut, EnvelopeSimple, Globe, Buildings, CalendarBlank, Tray } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";

export default function AdminDashboardPage() {
    const [inquiries, setInquiries] = useState(null);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    usePageMeta("Inquiries Dashboard | GoMedVoyage", "GoMedVoyage inquiries dashboard.");

    useEffect(() => {
        const token = localStorage.getItem("gmv_token");
        if (!token) {
            navigate("/admin/login");
            return;
        }
        api.get("/admin/inquiries", { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setInquiries(res.data))
            .catch((err) => {
                if (err.response?.status === 401) {
                    localStorage.removeItem("gmv_token");
                    navigate("/admin/login");
                } else {
                    setError("Could not load inquiries.");
                    setInquiries([]);
                }
            });
    }, [navigate]);

    const logout = () => {
        localStorage.removeItem("gmv_token");
        navigate("/admin/login");
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-tint-slate">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
                    <img src="/images/logo.png" alt="GoMedVoyage" className="h-10 w-auto" />
                    <button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-brand-blue hover:bg-slate-50 transition-colors"
                    >
                        <SignOut size={16} /> Sign Out
                    </button>
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
                <div className="flex items-end justify-between gap-6">
                    <div>
                        <h1 className="font-heading text-3xl font-semibold text-brand-blue">Inquiries</h1>
                        <p className="mt-1 text-sm text-slate-500" data-testid="inquiries-count">
                            {inquiries ? `${inquiries.length} total` : "Loading…"}
                        </p>
                    </div>
                </div>

                {error && <p data-testid="inquiries-error" className="mt-6 text-sm text-red-600">{error}</p>}

                {inquiries && inquiries.length === 0 && !error && (
                    <div data-testid="inquiries-empty" className="mt-12 rounded-3xl border border-dashed border-slate-200 bg-white p-16 text-center">
                        <Tray size={40} className="mx-auto text-slate-300" />
                        <p className="mt-4 font-heading text-lg font-medium text-slate-600">No inquiries yet</p>
                        <p className="mt-1 text-sm text-slate-400">New website inquiries will appear here.</p>
                    </div>
                )}

                <div className="mt-8 space-y-4">
                    {inquiries?.map((inq) => (
                        <article key={inq.id} data-testid={`inquiry-card-${inq.id}`} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <h2 className="font-heading text-lg font-semibold text-brand-blue">{inq.name}</h2>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-500">
                                        <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={14} /> {inq.email}</span>
                                        <span className="inline-flex items-center gap-1.5"><Globe size={14} /> {inq.country}</span>
                                        {inq.organization && <span className="inline-flex items-center gap-1.5"><Buildings size={14} /> {inq.organization}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="rounded-full bg-tint-green px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-green">{inq.interest}</span>
                                    <span className="inline-flex items-center gap-1.5 text-xs text-slate-400">
                                        <CalendarBlank size={13} />
                                        {new Date(inq.created_at).toLocaleString()}
                                    </span>
                                </div>
                            </div>
                            <p className="mt-4 text-sm leading-relaxed text-slate-600 whitespace-pre-wrap border-t border-slate-50 pt-4">{inq.message}</p>
                        </article>
                    ))}
                </div>
            </main>
        </div>
    );
}
